// Searchable combobox — replaces the plain MUI Select for endpoint-backed fields.
// Filters loaded options client-side (no new API calls). Same value/onChange contract.

import { Autocomplete, TextField } from '@mui/material';
import type { SelectOption } from '../api/client';

export function Combobox({
  label,
  value,
  options,
  onChange,
  error,
  helperText,
  required,
  loading,
  multiple = false,
  disabled = false
}: {
  label: string;
  value: string | number | Array<string | number>;
  options: SelectOption[];
  onChange: (value: unknown) => void;
  error?: string;
  helperText?: string;
  required?: boolean;
  loading?: boolean;
  multiple?: boolean;
  disabled?: boolean;
}) {
  // For single-select: find the matching option object (Autocomplete needs the object, not just the value).
  const selected = multiple
    ? (options.filter((opt) => Array.isArray(value) && value.includes(opt.value)) as SelectOption[])
    : options.find((opt) => opt.value === value) ?? null;

  return (
    <Autocomplete
      size="small"
      multiple={multiple}
      options={options}
      loading={loading}
      disabled={disabled}
      value={selected as unknown as null}
      onChange={(_event, newValue) => {
        if (multiple) {
          onChange((newValue as SelectOption[])?.map((o) => o.value) ?? []);
        } else {
          onChange((newValue as SelectOption | null)?.value ?? '');
        }
      }}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      getOptionLabel={(opt) => opt.label ?? String(opt.value)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(error)}
          helperText={error ?? helperText}
        />
      )}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (!q) return opts;
        return opts.filter((opt) => opt.label.toLowerCase().includes(q));
      }}
      noOptionsText={loading ? 'Loading...' : 'No matches'}
      sx={{ transition: (theme) => theme.transitions.create(['border-color'], { duration: 150 }) }}
    />
  );
}
